// src/pages/admin/AdminAnalytics.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/apiService';
import {
  ChartBarIcon,
  UsersIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

const AdminAnalytics = () => {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: () => apiService.getDashboardAnalytics(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const metrics = [
    {
      name: 'Total Users',
      value: analytics?.total_users || 0,
      change: analytics?.user_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
    },
    {
      name: 'Admin Users',
      value: analytics?.admin_count || 0,
      change: analytics?.admin_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
    },
    {
      name: 'Candidates',
      value: analytics?.candidate_count || 0,
      change: analytics?.candidate_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
    },
    {
      name: 'Active Users',
      value: analytics?.active_count || 0,
      change: analytics?.active_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
    },
    {
      name: 'Total Interviews',
      value: analytics?.total_interviews || 0,
      change: analytics?.interview_growth_percentage || null,
      changeType: 'positive',
      icon: VideoCameraIcon,
    },
    {
      name: 'Total Resumes',
      value: analytics?.total_resumes || 0,
      change: analytics?.resume_growth_percentage || null,
      changeType: 'positive',
      icon: DocumentTextIcon,
    },
    {
      name: 'Job Descriptions',
      value: analytics?.total_job_descriptions || 0,
      change: analytics?.job_growth_percentage || null,
      changeType: 'positive',
      icon: DocumentTextIcon,
    },
    {
      name: 'Recent Registrations',
      value: analytics?.recent_registrations || 0,
      change: analytics?.registration_growth_percentage || null,
      changeType: 'positive',
      icon: ArrowTrendingUpIcon,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600">System performance metrics and insights.</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <div key={metric.name} className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <metric.icon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                {metric.change && (
                  <div className="flex items-center text-sm">
                    {metric.changeType === 'positive' ? (
                      <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                    )}
                    <span className={metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}>
                      {metric.change}
                    </span>
                    <span className="text-gray-500 ml-1">vs last month</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">User Growth</h3>
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">Chart visualization would be implemented here</p>
          </div>
        </div>

        {/* Interview Statistics */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Interview Statistics</h3>
            <VideoCameraIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Completed Interviews</span>
              <span className="text-sm font-medium text-gray-900">
                {analytics?.interview_stats?.completed_percentage || '0'}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full" style={{ width: `${analytics?.interview_stats?.completed_percentage || 0}%` }}></div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">In Progress</span>
              <span className="text-sm font-medium text-gray-900">
                {analytics?.interview_stats?.in_progress_percentage || '0'}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-yellow-600 h-2 rounded-full" style={{ width: `${analytics?.interview_stats?.in_progress_percentage || 0}%` }}></div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Scheduled</span>
              <span className="text-sm font-medium text-gray-900">
                {analytics?.interview_stats?.scheduled_percentage || '0'}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${analytics?.interview_stats?.scheduled_percentage || 0}%` }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {analytics?.recent_activity?.length > 0 ? (
            analytics.recent_activity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{activity.action || 'Activity'}</p>
                  <p className="text-xs text-gray-500">{activity.user || activity.user_email || 'Unknown user'}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time || activity.created_at || 'Unknown time'}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Average Interview Score</h4>
          <p className="text-3xl font-bold text-primary-600">
            {analytics?.performance?.average_score || '0'}/10
          </p>
          {analytics?.performance?.score_change && (
            <p className="text-sm text-green-600 flex items-center mt-1">
              <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
              {analytics.performance.score_change} from last month
            </p>
          )}
        </div>

        <div className="card">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Resume Processing Time</h4>
          <p className="text-3xl font-bold text-primary-600">
            {analytics?.performance?.processing_time || '0'} min
          </p>
          {analytics?.performance?.processing_improvement && (
            <p className="text-sm text-green-600 flex items-center mt-1">
              <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
              {analytics.performance.processing_improvement} faster
            </p>
          )}
        </div>

        <div className="card">
          <h4 className="text-sm font-medium text-gray-700 mb-2">System Uptime</h4>
          <p className="text-3xl font-bold text-primary-600">
            {analytics?.performance?.uptime || '0'}%
          </p>
          <p className="text-sm text-gray-500 mt-1">Last 30 days</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;